import React, { useEffect, useRef, useCallback } from 'react';
import { PostCard } from './PostCard';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FadeInItem } from '@/components/motion/Animations';

export function FeedList({
  posts,
  loading,
  error,
  hasMore,
  onLoadMore,
  onLike,
  onComment,
  onReport,
  onEdit,
  onDelete,
  currentUserId,
  isAdmin,
}) {
  const observerRef = useRef();
  const loadingRef = useRef(null);

  // Infinite scroll observer
  const lastPostRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          onLoadMore();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, onLoadMore]
  );

  // Initial loading state
  if (loading && posts.length === 0) {
    return (
      <div className="flex justify-center items-center py-12" data-testid="feed-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error && posts.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 text-center"
        data-testid="feed-error"
        role="alert"
      >
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold mb-2">Erro ao carregar feed</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={onLoadMore} variant="outline">
          Tentar novamente
        </Button>
      </div>
    );
  }

  // Empty state
  if (!loading && posts.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 text-center"
        data-testid="feed-empty"
      >
        <div className="text-6xl mb-4">📝</div>
        <h3 className="text-lg font-semibold mb-2">Nenhum post ainda</h3>
        <p className="text-muted-foreground">
          Seja o primeiro a compartilhar algo com a comunidade!
        </p>
      </div>
    );
  }

  return (
    <div data-testid="feed-list" role="feed" aria-label="Feed de posts">
      {posts.map((post, index) => {
        // Attach ref to the last post for infinite scroll
        const isLastPost = index === posts.length - 1;
        return (
          <FadeInItem key={post.id} index={index}>
            <div ref={isLastPost ? lastPostRef : null}>
              <PostCard
                post={post}
                onLike={onLike}
                onComment={onComment}
                onReport={onReport}
                onEdit={onEdit}
                onDelete={onDelete}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
              />
            </div>
          </FadeInItem>
        );
      })}

      {/* Loading more indicator */}
      {loading && (
        <div
          className="flex justify-center items-center py-8"
          ref={loadingRef}
          data-testid="feed-loading-more"
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* No more posts indicator */}
      {!hasMore && posts.length > 0 && (
        <div
          className="text-center py-8 text-muted-foreground text-sm"
          data-testid="feed-end"
        >
          Você viu todos os posts disponíveis
        </div>
      )}
    </div>
  );
}
